import React, { useState } from "react";
import { FaHeadphones, FaCheckCircle, FaSave, FaSpinner } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Props {
  examSet: any;
  onUploadComplete: () => void;
}

export const HorenAudioUpload: React.FC<Props> = ({
  examSet,
  onUploadComplete,
}) => {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  if (!examSet) return null;

  const handleUpload = async () => {
    if (!audioFile) return alert("Select audio file first");

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", audioFile);

      const res = await fetch(
        `${API_BASE_URL}/api/admin/exams/${examSet.id}/upload-horen-audio`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (res.ok) {
        alert("✅ Hören audio uploaded!");
        setAudioFile(null);
        onUploadComplete();
      } else {
        const data = await res.json();
        alert(data.message || "Failed");
      }
    } catch (err) {
      alert("Network error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
          <FaHeadphones size={24} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            Hören Module Audio File
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            Upload ONE audio file for entire Hören module. Students play it{" "}
            <strong>ONCE</strong> during exam.
          </p>

          {examSet.horenAudioUrl ? (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-semibold flex items-center gap-2">
                  <FaCheckCircle className="text-green-600" />✅ Audio uploaded!
                </p>
              </div>

              {examSet.horenAudioStreamUrl && (
                <div className="p-3 bg-white border border-purple-200 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">Preview:</p>
                  <audio controls className="w-full" controlsList="nodownload">
                    <source
                      src={examSet.horenAudioStreamUrl}
                      type="audio/mpeg"
                    />
                  </audio>
                </div>
              )}

              <details className="text-sm">
                <summary className="cursor-pointer text-purple-600 hover:text-purple-700 font-semibold">
                  Replace audio
                </summary>
                <div className="mt-3 space-y-3">
                  <input
                    type="file"
                    accept="audio/*,video/mp4"
                    onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  {audioFile && (
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaSave />
                          Upload
                        </>
                      )}
                    </button>
                  )}
                </div>
              </details>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-semibold">
                  ⚠️ No audio yet
                </p>
              </div>

              <input
                type="file"
                accept="audio/*,video/mp4"
                onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />

              {audioFile && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Upload
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
